/** @format */

import dayjs from "dayjs";
import Linkify from "linkify-react";
import { type FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { fetchTranslation, getUserHandle } from "senf-shared";
import i18n from "senf-shared/utils/i18n";
import styled from "styled-components";
import theme from "../../../styles/theme";
import Box from "../../atoms/box/Box";
import Typography from "../../atoms/typography/Typography";
import Avatar from "../../core/avatar/Avatar";
import Dropdown from "../../core/dropdown/Dropdown";
import { useModals } from "../modalStack/ModalProvider";
import type { CommentCardProps } from "./CommentCard.types";

const Wrapper = styled.div<CommentCardProps>`
  float: left;
  position: relative;
  box-sizing: border-box;
  width: 100%;
  height: auto;
  max-width: 100%;
  border-top: 1px solid ${({ theme }) => theme.colors.brown.brown20tra};

  animation: opacityTranslateYFrom50Animation 0.8s;
`;

const InnerWrapper = styled.div`
  margin: 12px 0px 12px 0px;
`;

const CommentCard: FC<CommentCardProps> = ({
  data,
  handle,
  user,
  project,
  ...props
}) => {
  const { t } = useTranslation();
  const {
    title,
    createdAt,
    userHandle,
    userId,
    isAnonymous,
    commentId,
    photoURL,
    translations,
    lang,
  } = data;
  const { openModal } = useModals();

  const reportable =
    (handle?.reportComment && user?.userId !== userId) || !user?.userId;

  const deletable =
    handle?.deleteComment &&
    (user?.userId === userId ||
      user?.isAdmin === true ||
      user?.isModerator === true ||
      user?.isSuperAdmin === true);

  const inOriginalLanguage =
    project?.settings?.originalLanguage === i18n.resolvedLanguage;

  const [translatedTitle, setTranslatedTitle] = useState(null);

  useEffect(() => {
    if (
      project?.settings?.originalLanguage &&
      !inOriginalLanguage &&
      props?.shouldTranslate &&
      lang !== i18n.resolvedLanguage
    ) {
      fetchTranslation(project, data)
        .then((translated) => {
          setTranslatedTitle(translated); // Set the translation once it's resolved
        })
        .catch((error) => {
          console.error("Error fetching translation:", error);
          // Handle any errors, e.g., set a fallback translation or UI state
          setTranslatedTitle(title); // Fallback to the original title on error
        });
    } else {
      setTranslatedTitle(null);
    }
  }, [project, title, i18n.resolvedLanguage, props?.shouldTranslate]); // Depend on project and title to re-fetch translation if they change

  return (
    <Wrapper>
      <InnerWrapper>
        <Box justifyContent="space-between">
          <Box alignItems="center" height="40px" gap="10px">
            <Avatar
              alt={getUserHandle(isAnonymous, userId, userHandle)}
              size="sm"
              src={photoURL}
              onClick={
                userHandle !== null
                  ? () => handle?.openProfilePage(userId)
                  : undefined
              }
            />
            <Box flexDirection="column" marginTop="4px">
              <Typography
                variant="bodySm"
                color={theme.colors.black.black40tra}
              >
                {dayjs(createdAt).format("DD.MM.YYYY")}
              </Typography>
              <Typography
                variant="buttonBg"
                onClick={
                  userHandle !== null
                    ? () => handle?.openProfilePage(userId)
                    : undefined
                }
                style={{ cursor: userHandle !== null ? "pointer" : "default" }}
              >
                {!userId
                  ? t("global:deletedUser")
                  : getUserHandle(isAnonymous, userId, userHandle)}
              </Typography>
            </Box>
          </Box>

          <Dropdown
            variant="shapeless"
            size="md"
            leadingIcon="more"
            trailingIcon={false}
            items={[
              reportable && {
                leadingIcon: "alert",
                text: t("util:report", { val: t("global:comment") }),
                variant: "critical",
                onClick: () => handle?.reportComment(data),
              },
              deletable && {
                leadingIcon: "trash",
                text: t("util:delete", { val: t("global:comment") }),
                variant: "critical",
                onClick: () => {
                  openModal(null, {
                    title: t("global:deleteCommentConfirm"),
                    cancelText: t("global:cancel"),
                    submitText: t("util:delete", { val: t("global:comment") }),
                    critical: true,
                    onSubmit: () => handle?.deleteComment(data),
                  });
                },
              },
            ].filter(Boolean)}
          />
        </Box>
        <Box
          flexDirection="column"
          margin="4px 0px 8px 0px"
          width="100%"
          maxWidth="100%"
        >
          <Typography variant="bodyBg">
            <Linkify>
              {translatedTitle ||
                translations?.[i18n.resolvedLanguage]?.title ||
                title}
            </Linkify>
          </Typography>
        </Box>
      </InnerWrapper>
    </Wrapper>
  );
};

export default CommentCard;
